import { useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PROPULSION_TYPE_LABELS,
  PropulsionType,
  vehicleSchema,
  type VehicleData,
} from '@fleet/shared';
import { vehiclesApi, type VehicleDetail } from '../../api/vehicles';
import { vehicleGroupsApi } from '../../api/vehicle-groups';
import { Modal } from '../../ui/Modal';
import { Field, Fieldset, inputClass } from '../../ui/Field';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { formatCentAsEuro, parseEuroToCent } from './money';
import { toOptionalNumber, toOptionalText } from './field-values';

/**
 * Formularwerte, von Hand typisiert — nicht `z.input<typeof vehicleSchema>`.
 *
 * Zod 4 gibt `z.coerce.*` den Eingabetyp `unknown`. Der Ableitungstyp wäre für
 * `grossListPriceCent` und die drei Datumsfelder also `unknown` und damit als
 * Formulartyp unbrauchbar — `register` und `defaultValues` verlören jede
 * Prüfung. Hier steht deshalb, was das DOM tatsächlich liefert: Strings aus
 * Textfeldern, Zahlen aus den umgerechneten Feldern.
 */
interface VehicleFormValues {
  licensePlate: string;
  displayName: string;
  vehicleGroupId: string;
  make?: string;
  model?: string;
  vin?: string;
  seats?: number;
  notes?: string;
  colorHex?: string;
  grossListPriceCent?: number;
  firstRegistrationDate?: string;
  propulsionType: PropulsionType;
  co2GramsPerKm?: number;
  electricRangeKm?: number;
  privateUseAllowed: boolean;
  inServiceFrom: string;
  inServiceTo?: string;
  isActive: boolean;
}

const optionalText = { setValueAs: toOptionalText };
const optionalNumber = { setValueAs: toOptionalNumber };

function toFormValues(vehicle: VehicleDetail | undefined, fallbackGroupId: string): VehicleFormValues {
  if (!vehicle) {
    return {
      licensePlate: '',
      displayName: '',
      vehicleGroupId: fallbackGroupId,
      propulsionType: PropulsionType.VERBRENNER,
      privateUseAllowed: false,
      inServiceFrom: new Date().toISOString().slice(0, 10),
      isActive: true,
    };
  }

  return {
    licensePlate: vehicle.licensePlate,
    displayName: vehicle.displayName,
    vehicleGroupId: vehicle.vehicleGroupId,
    make: vehicle.make,
    model: vehicle.model,
    vin: vehicle.vin,
    seats: vehicle.seats,
    notes: vehicle.notes,
    // Der Rohwert, nicht die effektive Farbe — sonst würde die Vererbung der
    // Gruppenfarbe beim ersten Speichern stillschweigend festgeschrieben.
    colorHex: vehicle.colorHex,
    grossListPriceCent: vehicle.grossListPriceCent,
    firstRegistrationDate: vehicle.firstRegistrationDate,
    propulsionType: vehicle.propulsionType,
    co2GramsPerKm: vehicle.co2GramsPerKm,
    electricRangeKm: vehicle.electricRangeKm,
    privateUseAllowed: vehicle.privateUseAllowed,
    inServiceFrom: vehicle.inServiceFrom,
    inServiceTo: vehicle.inServiceTo,
    isActive: vehicle.isActive,
  };
}

export function VehicleForm({
  vehicle,
  fallbackGroupId,
  onClose,
}: {
  vehicle?: VehicleDetail;
  fallbackGroupId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ['vehicle-groups'], queryFn: vehicleGroupsApi.list });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<VehicleFormValues, unknown, VehicleData>({
    // Die eine Zusicherung überbrückt genau die `unknown`-Lücke aus dem
    // Kommentar oben; alles andere bleibt typgeprüft.
    resolver: zodResolver(vehicleSchema) as unknown as Resolver<VehicleFormValues, unknown, VehicleData>,
    defaultValues: toFormValues(vehicle, fallbackGroupId),
  });

  /**
   * Auf Wahrheitswert geprüft und nicht auf `!== undefined`.
   *
   * Ein leeres Farbfeld kann als `undefined`, als `null` aus der Datenbank oder
   * als Leerstring aus dem DOM auftauchen. Alle drei heißen dasselbe — „erbt von
   * der Gruppe" —, und ein Vergleich, der nur einen davon trifft, lässt das
   * Häkchen beim Bearbeiten leer.
   */
  const usesOwnColor = Boolean(watch('colorHex'));
  const selectedGroup = groups.data?.find((group) => group.id === watch('vehicleGroupId'));

  /**
   * Der Preis wird in Euro eingegeben und in Cent gehalten — zwei Darstellungen,
   * und deshalb ein eigener Anzeigezustand.
   *
   * Der naheliegende Weg, das Feld zu registrieren und mit `defaultValue` den
   * Euro-Text vorzugeben, geht nicht auf: React Hook Form schreibt beim
   * Registrieren seinen eigenen Wert ins DOM und überschreibt die Vorgabe. Im
   * Feld stand danach der **Cent**-Betrag, und weil beim Speichern erneut mit
   * hundert multipliziert wurde, wuchs der Preis bei jedem Bearbeiten um zwei
   * Nullen.
   *
   * Hier ist die Aufteilung eindeutig: Der Anzeigezustand hält den Text, den
   * jemand tippt, und schreibt bei jeder Änderung die Cent-Zahl ins Formular.
   */
  const [priceEuro, setPriceEuro] = useState(() =>
    formatCentAsEuro(vehicle?.grossListPriceCent),
  );

  const save = useMutation({
    mutationFn: async (values: VehicleData) => {
      if (vehicle) await vehiclesApi.update(vehicle.id, values);
      else await vehiclesApi.create(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      onClose();
    },
  });

  return (
    <Modal
      title={vehicle ? `${vehicle.licensePlate} bearbeiten` : 'Neues Fahrzeug'}
      description="Beim Speichern wird der gesamte Datensatz ersetzt — die Felder zeigen deshalb den vollständigen Bestand."
      onClose={onClose}
      size="lg"
    >
      <form
        onSubmit={(event) => {
          void handleSubmit((values) => save.mutateAsync(values))(event);
        }}
        className="space-y-4"
      >
        <Fieldset legend="Fahrzeug">
          <Field label="Kennzeichen" error={errors.licensePlate?.message}>
            <input {...register('licensePlate')} className={inputClass} autoFocus />
          </Field>

          <Field label="Bezeichnung" error={errors.displayName?.message}>
            <input {...register('displayName')} className={inputClass} />
          </Field>

          <Field label="Fahrzeuggruppe" error={errors.vehicleGroupId?.message}>
            <select {...register('vehicleGroupId')} className={inputClass}>
              <option value="">— bitte wählen —</option>
              {groups.data
                ?.filter((group) => group.isActive || group.id === vehicle?.vehicleGroupId)
                .map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                    {group.isActive ? '' : ' (deaktiviert)'}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Sitzplätze" optional error={errors.seats?.message}>
            <input type="number" {...register('seats', optionalNumber)} className={inputClass} />
          </Field>

          <Field label="Hersteller" optional error={errors.make?.message}>
            <input {...register('make', optionalText)} className={inputClass} />
          </Field>

          <Field label="Modell" optional error={errors.model?.message}>
            <input {...register('model', optionalText)} className={inputClass} />
          </Field>

          <Field
            label="Fahrgestellnummer"
            optional
            hint="17 Zeichen, ohne I, O und Q."
            error={errors.vin?.message}
          >
            <input {...register('vin', optionalText)} className={inputClass} />
          </Field>

          <Field label="Farbe" optional error={errors.colorHex?.message}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4"
                checked={!usesOwnColor}
                onChange={(event) =>
                  setValue(
                    'colorHex',
                    event.target.checked ? undefined : (selectedGroup?.colorHex ?? '#3b82f6'),
                    { shouldDirty: true },
                  )
                }
              />
              Farbe der Gruppe verwenden
            </label>
            {usesOwnColor && (
              <input type="color" {...register('colorHex')} className="mt-2 h-9 w-14 rounded border border-slate-300" />
            )}
          </Field>

          <div className="sm:col-span-2">
            <Field label="Notizen" optional error={errors.notes?.message}>
              <textarea {...register('notes', optionalText)} rows={2} className={inputClass} />
            </Field>
          </div>
        </Fieldset>

        <Fieldset legend="Steuerliche Stammdaten">
          <Field
            label="Bruttolistenpreis"
            optional
            hint="In Euro, inkl. Sonderausstattung und Umsatzsteuer."
            error={errors.grossListPriceCent?.message}
          >
            <input
              value={priceEuro}
              onChange={(event) => {
                setPriceEuro(event.target.value);
                setValue('grossListPriceCent', parseEuroToCent(event.target.value), {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              inputMode="decimal"
              placeholder="45.990,00"
              className={inputClass}
            />
          </Field>

          <Field label="Erstzulassung" optional error={errors.firstRegistrationDate?.message}>
            <input type="date" {...register('firstRegistrationDate', optionalText)} className={inputClass} />
          </Field>

          <Field label="Antriebsart" error={errors.propulsionType?.message}>
            <select {...register('propulsionType')} className={inputClass}>
              {Object.values(PropulsionType).map((type) => (
                <option key={type} value={type}>
                  {PROPULSION_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </Field>

          <Field label="CO₂ in g/km" optional error={errors.co2GramsPerKm?.message}>
            <input type="number" {...register('co2GramsPerKm', optionalNumber)} className={inputClass} />
          </Field>

          <Field label="Elektrische Reichweite in km" optional error={errors.electricRangeKm?.message}>
            <input type="number" {...register('electricRangeKm', optionalNumber)} className={inputClass} />
          </Field>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input {...register('privateUseAllowed')} type="checkbox" className="size-4" />
              Privatfahrten erlaubt
            </label>
          </div>
        </Fieldset>

        <Fieldset legend="Nutzungsdauer">
          <Field
            label="Im Dienst seit"
            hint="Das Fahrtenbuch muss diesen Zeitraum lückenlos abdecken."
            error={errors.inServiceFrom?.message}
          >
            <input type="date" {...register('inServiceFrom')} className={inputClass} />
          </Field>

          <Field label="Außer Dienst ab" optional error={errors.inServiceTo?.message}>
            <input type="date" {...register('inServiceTo', optionalText)} className={inputClass} />
          </Field>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input {...register('isActive')} type="checkbox" className="size-4" />
              Aktiv
            </label>
          </div>
        </Fieldset>

        {save.isError && <ErrorNotice error={save.error} />}

        {/* Bleibt am unteren Rand sichtbar. Sonst müsste man bei drei Blöcken
            erst ans Ende scrollen, um überhaupt speichern zu können. */}
        <div className="sticky bottom-0 -mx-6 -mb-5 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
